/**
 * ============================================================================
 * Iron Meridian - Globe Projection Hook
 * ============================================================================
 * 
 * Custom React hook for managing D3 orthographic (globe) projection.
 * Handles rotation, dragging, and provides memoized path generators.
 */

import { useState, useMemo, useCallback, useRef } from 'react'
import * as d3 from 'd3'

// Initial rotation to show Americas
const INITIAL_ROTATION = [90, -20, 0]

// Animation configuration
const ROTATION_SENSITIVITY = 0.35
const INERTIA_DECAY = 0.95
const INERTIA_THRESHOLD = 0.1

/**
 * Hook for globe projection management
 * @param {number} width - SVG width
 * @param {number} height - SVG height
 * @param {number} scale - Globe scale factor (0.4 = 40% of container)
 */
export function useGlobeProjection(width, height, scale = 0.42) {
  const [rotation, setRotation] = useState(INITIAL_ROTATION)
  const [isDragging, setIsDragging] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [hasInertia, setHasInertia] = useState(false)
  const dragStartRef = useRef(null)
  const rotationStartRef = useRef(null)
  const velocityRef = useRef({ x: 0, y: 0 })
  const lastMoveRef = useRef({ x: 0, y: 0, time: 0 })
  const animationRef = useRef(null)

  // Calculate globe radius based on container size
  const radius = useMemo(() => {
    return Math.min(width, height) * scale
  }, [width, height, scale])

  // Create orthographic projection - memoized for performance
  const projection = useMemo(() => {
    return d3.geoOrthographic()
      .translate([width / 2, height / 2])
      .scale(radius)
      .rotate(rotation)
      .clipAngle(90) // Visible hemisphere only
  }, [width, height, radius, rotation])

  // Create path generator - memoized
  const pathGenerator = useMemo(() => {
    return d3.geoPath().projection(projection)
  }, [projection])

  // Create graticule for globe grid lines
  const graticule = useMemo(() => {
    return d3.geoGraticule()
      .step([15, 15]) // 15 degree grid
      .extent([[-180, -90], [180, 90]])
  }, [])

  // Stop any running inertia animation
  const stopInertia = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = null
    }
    setHasInertia(false)
  }, [])

  // Apply inertia after drag ends
  const applyInertia = useCallback(() => {
    const { x, y } = velocityRef.current
    
    if (Math.abs(x) < INERTIA_THRESHOLD && Math.abs(y) < INERTIA_THRESHOLD) {
      setHasInertia(false)
      return
    }

    setHasInertia(true)

    const animate = () => {
      velocityRef.current.x *= INERTIA_DECAY
      velocityRef.current.y *= INERTIA_DECAY

      if (Math.abs(velocityRef.current.x) < INERTIA_THRESHOLD && 
          Math.abs(velocityRef.current.y) < INERTIA_THRESHOLD) {
        setHasInertia(false)
        return
      }

      setRotation(prev => {
        const newLon = prev[0] + velocityRef.current.x
        const newLat = Math.max(-90, Math.min(90, prev[1] - velocityRef.current.y))
        return [newLon, newLat, prev[2]]
      })

      animationRef.current = requestAnimationFrame(animate)
    }

    animate()
  }, [])

  // Handle drag start
  const handleDragStart = useCallback((clientX, clientY) => {
    stopInertia()
    setIsDragging(true)
    dragStartRef.current = { x: clientX, y: clientY }
    rotationStartRef.current = [...rotation]
    velocityRef.current = { x: 0, y: 0 }
    lastMoveRef.current = { x: clientX, y: clientY, time: Date.now() }
  }, [rotation, stopInertia])

  // Handle drag move
  const handleDragMove = useCallback((clientX, clientY) => {
    if (!isDragging || !dragStartRef.current || !rotationStartRef.current) return

    const dx = clientX - dragStartRef.current.x
    const dy = clientY - dragStartRef.current.y

    // Calculate velocity for inertia
    const now = Date.now()
    const dt = now - lastMoveRef.current.time
    if (dt > 0) {
      velocityRef.current = {
        x: ((clientX - lastMoveRef.current.x) / dt) * 16 * ROTATION_SENSITIVITY,
        y: ((clientY - lastMoveRef.current.y) / dt) * 16 * ROTATION_SENSITIVITY
      }
    }
    lastMoveRef.current = { x: clientX, y: clientY, time: now }

    // Update rotation
    const newLon = rotationStartRef.current[0] + dx * ROTATION_SENSITIVITY
    const newLat = Math.max(-90, Math.min(90, rotationStartRef.current[1] - dy * ROTATION_SENSITIVITY))
    
    setRotation([newLon, newLat, rotationStartRef.current[2]])
  }, [isDragging])

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(false)
    dragStartRef.current = null
    rotationStartRef.current = null
    applyInertia()
  }, [applyInertia])

  // Rotate to a specific country
  const rotateTo = useCallback((lon, lat, duration = 1000) => {
    stopInertia()
    setIsAnimating(true)
    
    const startRotation = [...rotation]
    const endRotation = [-lon, -lat, 0]
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const t = Math.min(elapsed / duration, 1)
      
      // Ease out cubic
      const ease = 1 - Math.pow(1 - t, 3)

      const currentRotation = [
        startRotation[0] + (endRotation[0] - startRotation[0]) * ease,
        startRotation[1] + (endRotation[1] - startRotation[1]) * ease,
        startRotation[2] + (endRotation[2] - startRotation[2]) * ease
      ]

      setRotation(currentRotation)

      if (t < 1) {
        animationRef.current = requestAnimationFrame(animate)
      } else {
        setIsAnimating(false)
      }
    }

    animate()
  }, [rotation, stopInertia])

  // Rotate by relative degrees with easing animation
  const rotateByAnimated = useCallback((dLon = 0, dLat = 0, duration = 400) => {
    stopInertia()
    setIsAnimating(true)

    const startRotation = [...rotation]
    const endRotation = [
      startRotation[0] + dLon,
      Math.max(-90, Math.min(90, startRotation[1] + dLat)),
      startRotation[2]
    ]
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const t = Math.min(elapsed / duration, 1)
      const ease = 1 - Math.pow(1 - t, 3) // ease-out cubic

      const currentRotation = [
        startRotation[0] + (endRotation[0] - startRotation[0]) * ease,
        startRotation[1] + (endRotation[1] - startRotation[1]) * ease,
        startRotation[2]
      ]

      setRotation(currentRotation)

      if (t < 1) {
        animationRef.current = requestAnimationFrame(animate)
      } else {
        setIsAnimating(false)
      }
    }

    animate()
  }, [rotation, stopInertia])

  // Reset to initial view
  const resetView = useCallback(() => {
    stopInertia()
    setRotation(INITIAL_ROTATION)
  }, [stopInertia])

  // Get centroid of a feature
  const getFeatureCentroid = useCallback((feature) => {
    if (!feature) return null
    return d3.geoCentroid(feature)
  }, [])

  // Check if a point is visible on the globe
  const isPointVisible = useCallback((lon, lat) => {
    const angle = d3.geoDistance(
      [-rotation[0], -rotation[1]],
      [lon, lat]
    )
    return angle < Math.PI / 2
  }, [rotation])

  return {
    projection,
    pathGenerator,
    graticule,
    rotation,
    setRotation,
    isDragging,
    isAnimating,
    hasInertia,
    radius,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    rotateTo,
    rotateByAnimated,
    resetView,
    getFeatureCentroid,
    isPointVisible
  }
}

export default useGlobeProjection
